<template>
  <v-dialog v-model="dialog" width="680" @click:outside="cancel" :persistent="isLoading">
    <v-card>
      <v-card-title>
        <h3><strong>{{ $t('tanaoroshiInventoryDateBulkDialog.label.title') }}</strong></h3>
      </v-card-title>
      <v-card-text>
        <div class="ml-4 mb-2" v-if="message" style="color: #2e7d32">
          {{ message }}
        </div>
        <div class="ml-4" v-if="errorMessage" style="color: red">
          {{ errorMessage }}
        </div>
        <!-- 行ごとの誤りは件数が多くなりうるので、スクロールできる枠にまとめる -->
        <div class="error_box ml-4 mt-1" v-if="errorList.length > 0">
          <div v-for="(data, idx) in errorList" :key="idx" style="color: red">
            {{ data.message }}
          </div>
        </div>

        <v-container>
          <v-row>
            <v-col cols="12">
              <div class="text-subtitle-2 mb-1">{{ $t('tanaoroshiInventoryDateBulkDialog.label.file') }}</div>
              <input
                ref="fileInput"
                type="file"
                accept=".xlsx"
                style="display: none"
                @change="onFileSelected"
              />
              <v-btn variant="outlined" :disabled="isLoading" @click="openFilePicker">
                {{ $t('common.action.upload') }}
              </v-btn>
              <span class="ml-3">{{ fileName }}</span>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <div class="format_box">
                <div class="text-subtitle-2 mb-1">{{ $t('tanaoroshiInventoryDateBulkDialog.label.formatTitle') }}</div>
                <ul class="pl-5">
                  <li>{{ $t('tanaoroshiInventoryDateBulkDialog.label.formatLine1') }}</li>
                  <li>{{ $t('tanaoroshiInventoryDateBulkDialog.label.formatLine2') }}</li>
                  <li>{{ $t('tanaoroshiInventoryDateBulkDialog.label.formatLine3') }}</li>
                  <li>{{ $t('tanaoroshiInventoryDateBulkDialog.label.formatLine4') }}</li>
                  <li>{{ $t('tanaoroshiInventoryDateBulkDialog.label.formatLine5') }}</li>
                </ul>
                <div class="text-caption mt-2">{{ $t('tanaoroshiInventoryDateBulkDialog.label.sample') }}</div>
                <table class="sample">
                  <thead>
                    <tr>
                      <th>{{ $t('tanaoroshiInventoryDateBulkDialog.label.sampleShop') }}</th>
                      <th>{{ $t('tanaoroshiInventoryDateBulkDialog.label.sampleDate') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>45538</td><td>2026-10-20</td></tr>
                    <tr><td>45538</td><td>2026-10-21</td></tr>
                    <tr><td>45601</td><td>2026-10-20</td></tr>
                  </tbody>
                </table>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn text @click="cancel" :disabled="isLoading">{{ $t('tanaoroshiInventoryDateBulkDialog.action.close') }}</v-btn>
        <v-btn
          color="indigo darken-1"
          dark
          :loading="isLoading"
          :disabled="!file"
          @click="importFile"
        >{{ $t('tanaoroshiInventoryDateBulkDialog.action.import') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import api from "@/apis/staff";
import constant from "@/utils/constant";
import utils from "@/utils";

export default {
  props: {
    handleCancel: {
      type: Function,
      required: true,
    },
  },

  computed: {
    fileName() {
      return this.file ? this.file.name : "";
    },
  },

  data() {
    return {
      dialog: true,
      isLoading: false,
      message: "",
      errorMessage: "",
      errorList: [],
      file: null,
    };
  },

  methods: {
    clearMessages() {
      this.message = "";
      this.errorMessage = "";
      this.errorList = [];
    },
    cancel() {
      if (this.isLoading) return;
      this.handleCancel();
    },
    openFilePicker() {
      this.$refs.fileInput.click();
    },
    onFileSelected() {
      this.clearMessages();
      this.file = this.$refs.fileInput.files[0] ?? null;
    },
    // 取込結果を受けて入力欄を空に戻す（同じファイルをもう一度選べるように値も消す）
    resetFile() {
      this.file = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = null;
    },
    importFile() {
      if (!this.file) return;
      this.isLoading = true;
      this.clearMessages();

      const formData = new FormData();
      formData.append("file", this.file);

      api
        .postFile("/tanaoroshi/inventory-date/bulk", formData)
        .then((response) => {
          if (response.data.status == constant.RESPONSE_STATUS.SUCCESS) {
            this.message = this.$t("tanaoroshiInventoryDateBulkDialog.message.success", {
              count: response.data.results?.registeredCount ?? 0,
            });
            this.resetFile();
            return;
          }
          // 誤りがあったときは1件も登録されていない。直して選び直してもらう
          this.errorMessage = response.data.errorMessage;
          this.errorList = response.data.errorList ?? [];
        })
        .catch((error) => {
          this.errorMessage = utils.getErrorMsg(error.response, this.$t);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.error_box {
  max-height: 160px;
  overflow: auto;
  padding: 6px 10px;
  border: solid 1px #ef9a9a;
  border-radius: 4px;
  background: #fff5f5;
}
.format_box {
  padding: 10px 14px;
  border: solid 1px #e0e0e0;
  border-radius: 4px;
  background: #fafafa;
  font-size: 0.875rem;
}
.sample {
  border-collapse: collapse;
  margin-top: 4px;
  th,
  td {
    border: solid 1px #bdbdbd;
    padding: 2px 14px;
    text-align: left;
  }
  th {
    background: #eeeeee;
  }
}
</style>
